import React from 'react';
import { Statistics } from './Home';
import { Button, Loading } from 'components/core';
import {
  ReviewStage,
  useStageContribution,
  useStageCounters,
} from 'query/review/ReviewStatistics';

export type StatisticsStatus = 'loading' | 'error' | 'success';

export type StageStatisticsStatus = {
  counters: StatisticsStatus;
  contribution: StatisticsStatus;
  retry: () => void;
  isRetrying: boolean;
};

type StatisticsQuery = {
  data?: unknown;
  isError: boolean;
  isFetching: boolean;
  errorUpdateCount: number;
};

// A failed background refetch keeps the values that already loaded, and a
// retry after a failure stays 'error' (react-query resets it to 'loading')
const statusOf = (query: StatisticsQuery): StatisticsStatus => {
  if (query.data !== undefined) return 'success';
  if (query.isError || query.errorUpdateCount > 0) return 'error';
  return 'loading';
};

const isRetrying = (query: StatisticsQuery) =>
  statusOf(query) === 'error' && query.isFetching;

export const UNAVAILABLE_STATISTIC = '—';

type StatisticValueProps = {
  status: StatisticsStatus;
  value: number;
};

export const StatisticValue = ({ status, value }: StatisticValueProps) => {
  if (status === 'loading') return <Loading size="xs" />;
  if (status === 'error') return <>{UNAVAILABLE_STATISTIC}</>;
  return <>{value}</>;
};

type RetryButtonProps = {
  status: StageStatisticsStatus;
  variant?: 'outline' | 'ghost';
  label?: string;
};

// Stays enabled while retrying so keyboard focus isn't lost
export const RetryButton = ({
  status,
  variant = 'outline',
  label,
}: RetryButtonProps) => (
  <Button
    size="sm"
    variant={variant}
    type={variant === 'ghost' ? 'brand' : 'neutral'}
    aria-label={label}
    aria-busy={status.isRetrying || undefined}
    onClick={status.isRetrying ? undefined : status.retry}
  >
    {status.isRetrying ? 'Retrying…' : 'Retry'}
  </Button>
);

export const studiesNoun = (status: StatisticsStatus, count: number) =>
  status === 'success' && count === 1 ? 'study' : 'studies';

export const emptyStatistics: Statistics = {
  contribution: 0,
  complete: 0,
  partiallyComplete: 0,
  available: 0,
  notStarted: 0,
  consensusRequired: 0,
};

export const useStageStatistics = (
  stage: ReviewStage
): { stats: Statistics; status: StageStatisticsStatus } => {
  const counters = useStageCounters(stage);
  const contribution = useStageContribution(stage);

  const retry = () => {
    if (statusOf(counters) === 'error') counters.refetch();
    if (statusOf(contribution) === 'error') contribution.refetch();
  };

  return {
    stats: {
      ...emptyStatistics,
      ...counters.data,
      contribution: contribution.data ?? 0,
    },
    status: {
      counters: statusOf(counters),
      contribution: statusOf(contribution),
      retry,
      isRetrying: isRetrying(counters) || isRetrying(contribution),
    },
  };
};
